import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from 'services/api';

function saApi() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('gss_sa_token')}` } };
}

const SANTE_CFG = {
  sain: { color: 'var(--gss-success)', label: 'SAIN', emoji: '\uD83D\uDFE2' },
  fragile: { color: 'var(--gss-warning)', label: 'FRAGILE', emoji: '\uD83D\uDFE1' },
  risque: { color: 'var(--gss-danger)', label: 'RISQUE', emoji: '\uD83D\uDD34' },
};

const TYPE_LABELS = { onboarding: 'Onboarding', appel: 'Appel', note: 'Note', bug: 'Bug', renouvellement: 'Renouvellement' };
const PRIO_COLORS = { critique: 'var(--gss-danger)', urgente: 'var(--gss-warning)', normale: 'var(--gss-info)', faible: 'var(--gss-text-light)' };

export default function SuperAdminCRM() {
  const [clients, setClients] = useState([]);
  const [alertes, setAlertes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [showNote, setShowNote] = useState(false);
  const [noteForm, setNoteForm] = useState({ type: 'note', notes: '', prochain_contact: '' });

  const load = () => {
    Promise.all([
      api.get('/super-admin/clients-suivi', saApi()),
      api.get('/super-admin/alertes-crm', saApi()),
    ]).then(([c, a]) => {
      setClients(c.data);
      setAlertes(a.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openFiche = async (id) => {
    setSelected(id);
    try {
      const r = await api.get(`/super-admin/clients-suivi/${id}`, saApi());
      setFiche(r.data);
    } catch { setFiche(null); }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    try {
      await api.post('/super-admin/clients-suivi/note', { ...noteForm, tenant_id: selected }, saApi());
      setShowNote(false);
      setNoteForm({ type: 'note', notes: '', prochain_contact: '' });
      openFiche(selected);
    } catch { alert('Erreur'); }
  };

  // KPIs
  const sains = clients.filter(c => c.sante === 'sain').length;
  const fragiles = clients.filter(c => c.sante === 'fragile').length;
  const risques = clients.filter(c => c.sante === 'risque').length;
  const appelsWeek = clients.filter(c => {
    if (!c.prochain_appel) return false;
    const d = new Date(c.prochain_appel);
    const now = new Date();
    const diff = (d - now) / 86400000;
    return diff >= 0 && diff <= 7;
  }).length;

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Link to="/super-admin" style={{ color: 'var(--gss-text-light)', fontSize: 12, textDecoration: 'none' }}>Dashboard</Link>
          <span style={{ color: 'var(--gss-text-light)', margin: '0 8px' }}>/</span>
          <span style={{ color: 'var(--gss-text)', fontSize: 14, fontWeight: 700 }}>CRM — Suivi clients</span>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 16, marginBottom: 24 }}>
        <KPI label="Clients sains" value={sains} color="var(--gss-success)" />
        <KPI label="Clients fragiles" value={fragiles} color="var(--gss-warning)" />
        <KPI label="Clients a risque" value={risques} color="var(--gss-danger)" />
        <KPI label="Appels cette semaine" value={appelsWeek} color="var(--gss-info)" />
      </div>

      {/* Alertes */}
      {alertes && (alertes.inactifs.length > 0 || alertes.impayes.length > 0 || alertes.tickets_anciens.length > 0) && (
        <div style={{ ...S.card, borderLeft: '3px solid var(--gss-danger)', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gss-danger)', marginBottom: 8 }}>Alertes actives</div>
          {alertes.inactifs.map((a, i) => (
            <div key={`i${i}`} style={S.alertLine}>{SANTE_CFG.risque.emoji} <strong>{a.nom}</strong> — inactif depuis 14+ jours</div>
          ))}
          {alertes.impayes.map((a, i) => (
            <div key={`p${i}`} style={S.alertLine}>{SANTE_CFG.risque.emoji} <strong>{a.tenant_nom}</strong> — facture impayee depuis 7+ jours ({a.numero})</div>
          ))}
          {alertes.tickets_anciens.map((a, i) => (
            <div key={`t${i}`} style={S.alertLine}>{SANTE_CFG.fragile.emoji} <strong>{a.tenant_nom}</strong> — ticket ouvert 48h+ : {a.titre}</div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 16, minWidth: 0 }}>
        {/* Table clients */}
        <div style={S.card}>
          <div className="gss-table-wrap">
          <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse' }}>
            <thead>
              {/* « MRR » -> « Revenu / mois » : aucun acronyme anglais a l'ecran. */}
              <tr>{['Societe','Offre','Agents','Revenu / mois','Derniere connexion','Sante','Tickets','Prochain appel',''].map(h =>
                <th key={h} style={S.th}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {clients.map(c => {
                const sc = SANTE_CFG[c.sante] || SANTE_CFG.sain;
                return (
                  <tr key={c.id} style={{ background: selected === c.id ? 'var(--gss-border)' : 'transparent', cursor: 'pointer' }} onClick={() => openFiche(c.id)}>
                    <td style={S.td}>
                      <div style={{ color: 'var(--gss-text)', fontWeight: 600, fontSize: 12 }}>{c.nom}</div>
                    </td>
                    <td style={{ ...S.td, fontSize: 11 }}>{c.offre || '-'}</td>
                    <td style={{ ...S.td, textAlign: 'center', fontWeight: 700 }}>{c.agents_actifs || 0}</td>
                    <td style={{ ...S.td, color: 'var(--gss-red)', fontWeight: 700, fontSize: 11 }}>{Number(c.mrr_calcule || 0).toLocaleString()}</td>
                    <td style={{ ...S.td, fontSize: 11, color: c.jours_inactif > 14 ? 'var(--gss-danger)' : 'var(--gss-text-light)' }}>
                      {c.derniere_connexion ? `il y a ${c.jours_inactif}j` : 'Jamais'}
                    </td>
                    <td style={S.td}>
                      <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                        background: `${sc.color}22`, color: sc.color }}>{sc.label}</span>
                    </td>
                    <td style={{ ...S.td, textAlign: 'center' }}>
                      {c.tickets_ouverts > 0 ? (
                        <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, color: 'var(--gss-danger)' }}>{c.tickets_ouverts}</span>
                      ) : <span style={{ color: 'var(--gss-text-light)', fontSize: 11 }}>0</span>}
                    </td>
                    <td style={{ ...S.td, fontSize: 11, color: 'var(--gss-text-light)' }}>
                      {c.prochain_appel ? new Date(c.prochain_appel).toLocaleDateString('fr-FR') : '-'}
                    </td>
                    <td style={S.td}>
                      <button onClick={(e) => { e.stopPropagation(); openFiche(c.id); }}
                        style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--gss-border-strong)', background: 'transparent', color: 'var(--gss-red)', fontSize: 10, cursor: 'pointer' }}>
                        Fiche
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          {loading && <p style={{ color: 'var(--gss-text-light)', padding: 20 }}>Chargement...</p>}
        </div>

        {/* Fiche client */}
        {selected && fiche && (
          <div style={{ ...S.card, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gss-text)' }}>{fiche.info.nom}</div>
              <button onClick={() => { setSelected(null); setFiche(null); }}
                style={{ background: 'transparent', border: 'none', color: 'var(--gss-text-light)', cursor: 'pointer', fontSize: 18 }}>x</button>
            </div>

            {/* Info */}
            <div style={{ padding: 12, background: 'var(--gss-bg)', borderRadius: 8, marginBottom: 12, fontSize: 12, color: 'var(--gss-text-secondary)' }}>
              <div>Email : {fiche.info.email_contact}</div>
              <div>Offre : <strong style={{ color: 'var(--gss-red)' }}>{fiche.info.offre || '-'}</strong></div>
              <div>Agents : {fiche.info.agents_actifs || 0} | Revenu / mois : {Number(fiche.info.mrr_calcule || 0).toLocaleString()} F</div>
              <div>Debut : {fiche.info.date_debut ? new Date(fiche.info.date_debut).toLocaleDateString('fr-FR') : '-'}</div>
              {/* ⚠️ « Engagement » DISAIT UNE DUREE POUR UNE CADENCE, et la colonne
                  qu'il lisait n'etait ecrite par personne (mesure du 13/09/2026 :
                  les trois lignes de production valaient « mensuel » par defaut).
                  La colonne est renommee `rythme_paiement` (051) et se pose
                  desormais depuis l'ecran Clients. Le libelle suit la notion. */}
              <div>Rythme de paiement : {fiche.info.rythme_paiement === 'annuel'
                ? `annuel — socle de ${Number(fiche.info.socle_mensuel || 0).toLocaleString('fr-FR')} F/mois`
                : (fiche.info.rythme_paiement || '-')}</div>
              {fiche.info.rythme_paiement === 'annuel' && fiche.info.date_renouvellement && (
                <div>Periode annuelle jusqu&apos;au {new Date(fiche.info.date_renouvellement).toLocaleDateString('fr-FR')}</div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button onClick={() => setShowNote(true)} style={S.btnSm}>+ Note de suivi</button>
              <button onClick={() => { setNoteForm({ type: 'appel', notes: '', prochain_contact: '' }); setShowNote(true); }} style={{ ...S.btnSm, background: 'var(--gss-info)' }}>Planifier appel</button>
            </div>

            {/* Factures */}
            {fiche.factures.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Factures</div>
                {fiche.factures.slice(0, 5).map(f => (
                  <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11, borderBottom: '1px solid var(--gss-border)' }}>
                    <span style={{ color: 'var(--gss-text-light)' }}>{f.mois}/{f.annee}</span>
                    <span style={{ color: 'var(--gss-text-secondary)' }}>{Number(f.total_ttc).toLocaleString()} F</span>
                    <span style={{ color: f.statut === 'payee' ? 'var(--gss-success)' : 'var(--gss-danger)', fontWeight: 700 }}>{f.statut}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Timeline suivi */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Timeline suivi</div>
              {fiche.suivi.length === 0 && <p style={{ fontSize: 11, color: 'var(--gss-text-light)' }}>Aucun suivi</p>}
              {fiche.suivi.map(s => (
                <div key={s.id} style={{ padding: '6px 0', borderLeft: '2px solid var(--gss-red)', paddingLeft: 12, marginBottom: 4 }}>
                  <div style={{ fontSize: 10, color: 'var(--gss-text-light)' }}>{new Date(s.date_suivi).toLocaleDateString('fr-FR')}</div>
                  <div style={{ fontSize: 12, color: 'var(--gss-text-secondary)' }}>
                    <span style={{ color: 'var(--gss-red)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{TYPE_LABELS[s.type] || s.type}</span> — {s.notes}
                  </div>
                  {s.prochain_contact && <div style={{ fontSize: 10, color: 'var(--gss-info)' }}>Prochain contact : {new Date(s.prochain_contact).toLocaleDateString('fr-FR')}</div>}
                </div>
              ))}
            </div>

            {/* Tickets */}
            {fiche.tickets.length > 0 && (
              <div>
                <div style={{ fontSize: 11, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Tickets support</div>
                {fiche.tickets.map(tk => (
                  <div key={tk.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 11, borderBottom: '1px solid var(--gss-border)' }}>
                    <span style={{ color: 'var(--gss-text-secondary)' }}>{tk.titre}</span>
                    <span style={{ color: PRIO_COLORS[tk.priorite], fontWeight: 700 }}>{tk.priorite}</span>
                    <span style={{ color: tk.statut === 'resolu' ? 'var(--gss-success)' : 'var(--gss-warning)' }}>{tk.statut}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal note */}
      {showNote && (
        <div style={S.overlay} onClick={() => setShowNote(false)}>
          <form onSubmit={submitNote} style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gss-text)', marginBottom: 16 }}>Ajouter un suivi</div>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Type</label>
              <select value={noteForm.type} onChange={e => setNoteForm({ ...noteForm, type: e.target.value })} style={S.input}>
                <option value="note">Note</option>
                <option value="appel">Appel</option>
                <option value="onboarding">Onboarding</option>
                <option value="bug">Bug</option>
                <option value="renouvellement">Renouvellement</option>
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Notes</label>
              <textarea value={noteForm.notes} onChange={e => setNoteForm({ ...noteForm, notes: e.target.value })} required
                style={{ ...S.input, height: 80, resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Prochain contact</label>
              <input type="date" value={noteForm.prochain_contact} onChange={e => setNoteForm({ ...noteForm, prochain_contact: e.target.value })} style={S.input} />
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowNote(false)} style={{ ...S.btnSm, background: 'transparent', border: '1px solid var(--gss-border-strong)', color: 'var(--gss-text-light)' }}>Annuler</button>
              <button type="submit" style={S.btnSm}>Enregistrer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <div style={S.kpiCard}>
      <div style={{ fontSize: 10, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 900, color }}>{value}</div>
    </div>
  );
}

const S = {
  page: { width: '100%', maxWidth: '100%', boxSizing: 'border-box', background: 'var(--gss-bg)', padding: 'clamp(12px, 3vw, 28px)' },
  card: { background: 'var(--gss-white)', borderRadius: 12, padding: 16, border: '1px solid var(--gss-border)', minWidth: 0 },
  kpiCard: { background: 'var(--gss-white)', borderRadius: 12, padding: 20, border: '1px solid var(--gss-border)', minWidth: 0 },
  th: { textAlign: 'left', padding: '6px 8px', fontSize: 9, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid var(--gss-border)' },
  td: { padding: '8px', borderBottom: '1px solid var(--gss-border)', fontSize: 12, color: 'var(--gss-text-secondary)' },
  btnSm: { padding: '6px 14px', borderRadius: 6, background: 'var(--gss-red)', color: '#FFF', fontWeight: 700, fontSize: 11, border: 'none', cursor: 'pointer' },
  alertLine: { fontSize: 12, color: 'var(--gss-text-secondary)', padding: '4px 0' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--gss-white)', borderRadius: 16, padding: 28, width: 420, border: '1px solid var(--gss-border-strong)' },
  label: { display: 'block', fontSize: 11, color: 'var(--gss-text-light)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 },
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--gss-border-strong)', background: 'var(--gss-bg)', color: 'var(--gss-text)', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
};
